import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import React from "react";
import { Link, useRoute } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { getBuyerGuide } from "@/data/buyerGuides";
import { siteConfig } from "@/data/site";
import NotFound from "./NotFound";

export default function BuyerGuide() {
  const [, params] = useRoute<{ slug: string }>("/resources/buyer-guides/:slug");
  const guide = getBuyerGuide(params?.slug);
  if (!guide) return <NotFound />;

  const canonicalPath = `/resources/buyer-guides/${guide.slug}`;
  const url = `${siteConfig.url}${canonicalPath}`;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
          { "@type": "ListItem", position: 2, name: "Resources", item: `${siteConfig.url}/resources` },
          { "@type": "ListItem", position: 3, name: "Buyer Guides", item: `${siteConfig.url}/resources/buyer-guides` },
          { "@type": "ListItem", position: 4, name: guide.title, item: url },
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: guide.title,
        description: guide.metaDescription,
        isPartOf: { "@id": `${siteConfig.url}/#website` },
        mainEntity: { "@id": `${url}#article` },
        breadcrumb: { "@id": `${url}#breadcrumb` },
      },
      {
        "@type": "Article",
        "@id": `${url}#article`,
        headline: guide.title,
        description: guide.metaDescription,
        author: { "@id": `${siteConfig.url}/#business` },
        publisher: { "@id": `${siteConfig.url}/#business` },
        mainEntityOfPage: { "@id": `${url}#webpage` },
      },
    ],
  };

  return (
    <>
      <StructuredData data={schema} />
      <header className="buyer-guide-article-hero section-line">
        <div>
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/resources">Resources</Link><span>/</span><Link href="/resources/buyer-guides">Buyer guides</Link><span>/</span>{guide.title}</p>
          <span className="kicker">{guide.kicker} · {guide.number}</span>
          <h1>{guide.title}</h1>
          <p>{guide.intro}</p>
          <div className="button-row"><a href="#guide-steps" className="button button--primary">Read the guide <ArrowRight aria-hidden="true" /></a><Link href="/request-a-quote" className="button button--outline">Request a quote <ArrowUpRight aria-hidden="true" /></Link></div>
        </div>
        <aside aria-label={`${guide.kicker} guide summary`}>
          <span>{guide.number}</span>
          <strong>Buyer Guide</strong>
          <p>Practical commercial restroom planning information from Partition Stalls.</p>
        </aside>
      </header>

      <main id="guide-steps" className="buyer-guide-steps section-line" aria-label={`${guide.title} steps`}>
        {guide.sections.map((section, index) => (
          <article key={section.title}>
            <div className="buyer-guide-steps__number">0{index + 1}</div>
            <div className="buyer-guide-steps__copy"><span className="kicker">Planning step</span><h2>{section.title}</h2><p>{section.copy}</p></div>
            <ul>{section.checks.map((check) => <li key={check}><Check aria-hidden="true" />{check}</li>)}</ul>
          </article>
        ))}
      </main>

      <section className="buyer-guide-next section-line" aria-labelledby="buyer-guide-next-title">
        <div><span className="kicker">Keep planning</span><h2 id="buyer-guide-next-title">Take the next useful step.</h2><p>Use the tools and guides that relate directly to this decision, then send the known information for project-specific review.</p></div>
        <nav aria-label={`Related links for ${guide.title}`}>
          {guide.nextSteps.map((step, index) => <Link href={step.href} key={step.href}><span>0{index + 1}</span><strong>{step.label}</strong><p>{step.copy}</p><ArrowRight aria-hidden="true" /></Link>)}
        </nav>
      </section>

      <QuoteBand title={guide.quoteTitle} copy={guide.quoteCopy} />
    </>
  );
}
